import { useState, useRef, useEffect } from 'react'
import { useCierreAlClicAfuera } from '@/lib/useCierreAlClicAfuera'
import { createPortal } from 'react-dom'
import { MoreVertical } from 'lucide-react'
import { IconButton } from './IconButton'

export interface KebabMenuItem {
  label: string
  icon?: React.ReactNode
  onClick: () => void
  className?: string
  disabled?: boolean
  hidden?: boolean
}

interface KebabMenuProps {
  items: KebabMenuItem[]
}

export function KebabMenu({ items }: KebabMenuProps) {
  const visibleItems = items.filter(i => !i.hidden)

  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0, openUp: false })
  const btnRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  useCierreAlClicAfuera(open, [btnRef, menuRef], () => setOpen(false))

  // Calcular posición al abrir (portal, fuera del overflow)
  useEffect(() => {
    if (!open || !btnRef.current) return
    const rect = btnRef.current.getBoundingClientRect()
    const menuH = 40 * visibleItems.length + 8 // estimación altura menú
    const viewH = window.innerHeight
    const openUp = rect.bottom + menuH > viewH - 8

    setPos({
      top: openUp ? rect.top - menuH : rect.bottom + 4,
      left: Math.max(8, rect.right - 168), // 168 ≈ min-w-[160px] + padding
      openUp,
    })
  }, [open, visibleItems.length])

  // Close on scroll (tabla con overflow-x-auto)
  useEffect(() => {
    if (!open) return
    const handler = () => setOpen(false)
    window.addEventListener('scroll', handler, true)
    return () => window.removeEventListener('scroll', handler, true)
  }, [open])

  // El `return null` va DESPUÉS de los hooks, como exige `rules-of-hooks`.
  // Estaba antes; en la práctica no rompía —React no detecta «menos hooks»
  // cuando un render no llama ninguno, solo reinicia el estado— pero la regla
  // marcaba 8 errores en este archivo y tapaba cualquier error nuevo.
  if (visibleItems.length === 0) return null

  return (
    <>
      <IconButton
        ref={btnRef}
        onClick={e => { e.stopPropagation(); setOpen(p => !p) }}
        aria-label="Más acciones"
        aria-haspopup="menu"
        aria-expanded={open}
        icon={<MoreVertical size={14} />}
      />
      {open && createPortal(
        <>
          <div
            ref={menuRef}
            className="fixed z-[100] bg-popover border border-divider rounded-xl shadow-lg py-1 min-w-[160px]"
            style={{
              top: pos.top,
              left: pos.left,
              backgroundColor: 'rgb(var(--color-popover))',
            }}
          >
            {visibleItems.map((item, i) => (
              <button
                key={i}
                onClick={e => { e.stopPropagation(); item.onClick(); setOpen(false) }}
                disabled={item.disabled}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors disabled:opacity-50
                  ${item.className || 'text-content hover:bg-soft'}`}
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </div>
        </>,
        document.body
      )}
    </>
  )
}
